/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2024.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Capability} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/Capability";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import FieldListMenuContent from "qqq/components/query/FieldListMenuContent";
import {sanitizeId} from "qqq/utils/qqqIdUtils";
import React, {useState} from "react";

interface FieldListMenuProps
{
   idPrefix: string,
   heading?: string,
   placeholder?: string,
   tableMetaData: QTableMetaData,
   showTableHeaderEvenIfNoExposedJoins: boolean,
   fieldNamesToHide?: string[],
   buttonProps: any,
   buttonChildren: JSX.Element | string,
   isModeSelectOne?: boolean,
   handleSelectedField?: (field: QFieldMetaData, table: QTableMetaData) => void,
   isModeToggle?: boolean,
   toggleStates?: { [fieldName: string]: boolean },
   handleToggleField?: (field: QFieldMetaData, table: QTableMetaData, newValue: boolean) => void,
   fieldEndAdornment?: JSX.Element,
   handleAdornmentClick?: (field: QFieldMetaData, table: QTableMetaData, event: React.MouseEvent<any>) => void,
   omitExposedJoins?: string[],
   requiredCapability?: Capability
}

FieldListMenu.defaultProps = {
   showTableHeaderEvenIfNoExposedJoins: false,
   isModeSelectOne: false,
   isModeToggle: false,
};

/*******************************************************************************
 ** Component to render a button which opens a menu containing a list of fields
 ** from a table (and its join tables) which can be interacted with.  The menu
 ** body is FieldListMenuContent (shared with the Record Screen field-jump
 ** modal); this component owns only the trigger button and the anchored menu.
 *******************************************************************************/
export default function FieldListMenu({idPrefix, heading, placeholder, tableMetaData, showTableHeaderEvenIfNoExposedJoins, buttonProps, buttonChildren, isModeSelectOne, fieldNamesToHide, handleSelectedField, isModeToggle, toggleStates, handleToggleField, fieldEndAdornment, handleAdornmentClick, omitExposedJoins, requiredCapability}: FieldListMenuProps): JSX.Element
{
   const [menuAnchorElement, setMenuAnchorElement] = useState(null);


   /*******************************************************************************
    **
    *******************************************************************************/
   function openMenu(event: any)
   {
      setMenuAnchorElement(event.currentTarget);
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function closeMenu()
   {
      setMenuAnchorElement(null);
   }


   return (
      <>
         <Button onClick={openMenu} data-qqq-id={`button-${sanitizeId(idPrefix)}`} {...buttonProps}>
            {buttonChildren}
         </Button>
         <Menu
            anchorEl={menuAnchorElement}
            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            transformOrigin={{vertical: "top", horizontal: "left"}}
            open={menuAnchorElement != null}
            onClose={closeMenu}
            keepMounted
         >
            <FieldListMenuContent
               idPrefix={idPrefix}
               heading={heading}
               placeholder={placeholder}
               tableMetaData={tableMetaData}
               showTableHeaderEvenIfNoExposedJoins={showTableHeaderEvenIfNoExposedJoins}
               fieldNamesToHide={fieldNamesToHide}
               isModeSelectOne={isModeSelectOne}
               handleSelectedField={handleSelectedField}
               isModeToggle={isModeToggle}
               toggleStates={toggleStates}
               handleToggleField={handleToggleField}
               fieldEndAdornment={fieldEndAdornment}
               handleAdornmentClick={handleAdornmentClick}
               omitExposedJoins={omitExposedJoins}
               requiredCapability={requiredCapability}
               isOpen={menuAnchorElement != null}
               onClose={closeMenu}
            />
         </Menu>
      </>
   );
}
